import { AppRole } from './roles';

export const PERMISSIONS = {
  PROJECT_ACCEPTANCE_ACCESS: 'project_acceptance:access',
  INSPECTION_ACCESS: 'inspection:access',
  INSPECTION_CREATE: 'inspection:create',
  INSPECTION_VIEW_ALL: 'inspection:view_all',
  INSPECTION_DELETE_SUBMITTED: 'inspection:delete_submitted',
  INSPECTION_RESTORE: 'inspection:restore',
  REQUEST_CREATE: 'request:create',
  REQUEST_VIEW_ALL: 'request:view_all',
  REQUEST_DELETE_SUBMITTED: 'request:delete_submitted',
  REQUEST_RESTORE: 'request:restore',
  CASE_VIEW: 'case:view',
  CASE_VIEW_ALL: 'case:view_all',
  CASE_UPDATE_DETAILS: 'case:update_details',
  CASE_AMEND_CLOSED: 'case:amend_closed',
  CASE_QUEUE_VIEW: 'case_queue:view',
  CASE_QUEUE_SELF_ASSIGN: 'case_queue:self_assign',
  CASE_QUEUE_MANAGE: 'case_queue:manage',
  REVIEW_SUBMIT: 'review:submit',
  REVIEW_OVERRIDE: 'review:override',
  REVIEW_CORRECTION: 'review:correction',
  CASE_FINALIZE: 'case:finalize',
  CASE_RESUBMIT: 'case:resubmit',
  USER_MANAGE: 'user:manage',
  ROLE_MANAGE: 'role:manage',
  DEADLINE_MANAGE: 'deadline:manage',
  MARKET_MANAGE: 'market:manage',
  DASHBOARD_VIEW: 'dashboard:view',
} as const;

export type AppPermission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS: ReadonlySet<string> = new Set<string>(Object.values(PERMISSIONS));

/**
 * Returns true if the given permission string is defined in the PERMISSIONS registry.
 */
export function isValidPermission(permission: string): permission is AppPermission {
  return ALL_PERMISSIONS.has(permission);
}

/**
 * Asserts that the given permission string exists in the PERMISSIONS registry.
 * Throws an Error if the permission is not defined.
 */
export function assertValidPermission(permission: string): asserts permission is AppPermission {
  if (!isValidPermission(permission)) {
    throw new Error(
      `Permission "${permission}" is not defined in the permission registry. Reference PERMISSIONS constants instead.`,
    );
  }
}

export interface PermissionChecker {
  hasPermission(permission: AppPermission | string): boolean;
}

/**
 * Resolves implied permissions from a given collection of permissions.
 * Holding `case:view_all` implicitly grants `case:view`.
 */
export function resolveImpliedPermissions(permissions: Iterable<string>): Set<string> {
  const resolved = new Set<string>(permissions);
  if (resolved.has(PERMISSIONS.CASE_VIEW_ALL)) {
    resolved.add(PERMISSIONS.CASE_VIEW);
  }
  return resolved;
}

/**
 * The Reviewer Dashboard serves pure reviewers. Users who can also override reviews (FTE)
 * or resubmit cases (vendor managers) have their own dashboards.
 */
export function hasReviewerDashboardAccess(checker: PermissionChecker): boolean {
  return (
    checker.hasPermission(PERMISSIONS.REVIEW_SUBMIT) &&
    !checker.hasPermission(PERMISSIONS.REVIEW_OVERRIDE) &&
    !checker.hasPermission(PERMISSIONS.CASE_RESUBMIT)
  );
}

/** Any of these lets a user see Case assignees; mirrors the backend `can_view_case_assignee`. */
export const CASE_ASSIGNEE_VISIBILITY_PERMISSIONS = [
  PERMISSIONS.CASE_VIEW_ALL,
  PERMISSIONS.CASE_QUEUE_VIEW,
  PERMISSIONS.CASE_QUEUE_MANAGE,
  PERMISSIONS.REVIEW_SUBMIT,
] as const;

/** Any of these lets a user list reviewers; mirrors the backend `can_list_reviewers`. */
export const REVIEWER_LISTING_PERMISSIONS = [
  PERMISSIONS.CASE_QUEUE_MANAGE,
  PERMISSIONS.CASE_VIEW_ALL,
] as const;

/**
 * Whether the user may filter Cases by Reviewer LDAP: see the control, load its options and
 * send `reviewer_in`. The options come from `/api/users/reviewers/`, so this needs the
 * reviewer-listing permissions; each of those also lets the user see Case assignees, which the
 * backend requires for `reviewer_in`. Users who may only see assignees get no filter at all,
 * so a shared `?reviewer=` link never leaves them a hidden filter they cannot remove.
 */
export function canFilterByReviewer(checker: PermissionChecker): boolean {
  return REVIEWER_LISTING_PERMISSIONS.some((permission) => checker.hasPermission(permission));
}

const ADMIN_PANEL_DEFAULT_ROUTES: readonly {
  readonly permission: AppPermission;
  readonly path: string;
}[] = [
  { permission: PERMISSIONS.USER_MANAGE, path: '/admin-panel' },
  { permission: PERMISSIONS.MARKET_MANAGE, path: '/admin-panel/market-management' },
  { permission: PERMISSIONS.ROLE_MANAGE, path: '/admin-panel/role-settings' },
  { permission: PERMISSIONS.DEADLINE_MANAGE, path: '/admin-panel/submission-deadlines' },
  { permission: PERMISSIONS.REQUEST_RESTORE, path: '/admin-panel/deleted-submissions' },
  { permission: PERMISSIONS.INSPECTION_RESTORE, path: '/admin-panel/deleted-inspections' },
];

/**
 * Returns true if the user holds any permission that grants access to the Admin Panel.
 */
export function canAccessAdminPanel(checker: PermissionChecker): boolean {
  return ADMIN_PANEL_DEFAULT_ROUTES.some(({ permission }) => checker.hasPermission(permission));
}

/**
 * Resolves the first Admin Panel route the user has permission to view,
 * matching the Admin Sidebar navigation order.
 */
export function resolveAdminPanelDefaultPath(checker: PermissionChecker): string {
  const match = ADMIN_PANEL_DEFAULT_ROUTES.find(({ permission }) =>
    checker.hasPermission(permission),
  );
  return match?.path ?? '/no-role';
}

const VIEW_PERMISSIONS: ReadonlySet<string> = new Set<string>([
  PERMISSIONS.PROJECT_ACCEPTANCE_ACCESS,
  PERMISSIONS.DASHBOARD_VIEW,
  PERMISSIONS.CASE_VIEW,
  PERMISSIONS.CASE_VIEW_ALL,
]);

/**
 * Read-only viewers see every case but can act on none, so they get a reduced nav.
 * Any permission beyond viewing (e.g. from an additive admin role) opts the user
 * into the full permission-driven nav.
 */
export function isReadOnlyViewer(checker: PermissionChecker): boolean {
  const holdsActionPermission = [...ALL_PERMISSIONS]
    .filter((permission) => !VIEW_PERMISSIONS.has(permission))
    .some((permission) => checker.hasPermission(permission));
  return (
    checker.hasPermission(PERMISSIONS.DASHBOARD_VIEW) &&
    checker.hasPermission(PERMISSIONS.CASE_VIEW_ALL) &&
    !holdsActionPermission
  );
}

/**
 * Creates a test double for permission checking that rejects permissions
 * absent from the registry, preventing tests from passing against non-existent permissions.
 */
export function createMockPermissionChecker(
  grantedPermissions: Iterable<string> = [],
): PermissionChecker {
  for (const p of grantedPermissions) {
    assertValidPermission(p);
  }
  const granted = resolveImpliedPermissions(grantedPermissions);

  return {
    hasPermission(permission: string): boolean {
      assertValidPermission(permission);
      return granted.has(permission);
    },
  };
}

/**
 * Creates a mock permission checking function that validates each checked permission
 * against the registry and returns true if granted.
 */
export function createMockPermissionFn(grantedPermissions: Iterable<string> = []) {
  const checker = createMockPermissionChecker(grantedPermissions);
  return (permission: string): boolean => checker.hasPermission(permission);
}

export const ROLE_PERMISSIONS: Record<AppRole, readonly string[]> = {
  construction_vendor: [
    PERMISSIONS.PROJECT_ACCEPTANCE_ACCESS,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.CASE_VIEW,
  ],
  reviewer: [
    PERMISSIONS.PROJECT_ACCEPTANCE_ACCESS,
    PERMISSIONS.CASE_VIEW,
    PERMISSIONS.CASE_QUEUE_VIEW,
    PERMISSIONS.CASE_QUEUE_SELF_ASSIGN,
    PERMISSIONS.CASE_UPDATE_DETAILS,
    PERMISSIONS.REVIEW_SUBMIT,
  ],
  case_allocator: [
    PERMISSIONS.PROJECT_ACCEPTANCE_ACCESS,
    PERMISSIONS.CASE_VIEW_ALL,
    PERMISSIONS.CASE_QUEUE_VIEW,
    PERMISSIONS.CASE_QUEUE_MANAGE,
    PERMISSIONS.CASE_UPDATE_DETAILS,
  ],
  vendor_manager: [
    PERMISSIONS.PROJECT_ACCEPTANCE_ACCESS,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.REQUEST_CREATE,
    PERMISSIONS.REQUEST_VIEW_ALL,
    PERMISSIONS.CASE_VIEW_ALL,
    PERMISSIONS.CASE_UPDATE_DETAILS,
    PERMISSIONS.REVIEW_CORRECTION,
    PERMISSIONS.CASE_RESUBMIT,
  ],
  acceptance_review_fte: [
    PERMISSIONS.PROJECT_ACCEPTANCE_ACCESS,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.REQUEST_VIEW_ALL,
    PERMISSIONS.CASE_VIEW_ALL,
    PERMISSIONS.CASE_QUEUE_VIEW,
    PERMISSIONS.CASE_UPDATE_DETAILS,
    PERMISSIONS.REVIEW_SUBMIT,
    PERMISSIONS.REVIEW_OVERRIDE,
    PERMISSIONS.REVIEW_CORRECTION,
    PERMISSIONS.CASE_FINALIZE,
    PERMISSIONS.CASE_RESUBMIT,
  ],
  read_only_viewer: [
    PERMISSIONS.PROJECT_ACCEPTANCE_ACCESS,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.CASE_VIEW_ALL,
  ],
  national_field_auditor: [
    PERMISSIONS.INSPECTION_ACCESS,
    PERMISSIONS.INSPECTION_CREATE,
    PERMISSIONS.INSPECTION_VIEW_ALL,
  ],
  regional_inspector: [
    PERMISSIONS.INSPECTION_ACCESS,
    PERMISSIONS.INSPECTION_CREATE,
    PERMISSIONS.INSPECTION_VIEW_ALL,
  ],
  inspection_manager: [PERMISSIONS.INSPECTION_ACCESS, PERMISSIONS.INSPECTION_VIEW_ALL],
  admin: [
    PERMISSIONS.INSPECTION_DELETE_SUBMITTED,
    PERMISSIONS.INSPECTION_RESTORE,
    PERMISSIONS.REQUEST_DELETE_SUBMITTED,
    PERMISSIONS.REQUEST_RESTORE,
    PERMISSIONS.CASE_AMEND_CLOSED,
    PERMISSIONS.USER_MANAGE,
    PERMISSIONS.ROLE_MANAGE,
    PERMISSIONS.DEADLINE_MANAGE,
    PERMISSIONS.MARKET_MANAGE,
  ],
};
